import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { getColumnId } from '@/types/table.types';

/** Tables that support CSV export. Keep in sync with wrappers passing tableName. */
export type CsvTableName = 'inventory' | 'trades' | 'availability';

/**
 * Compute column IDs in visual order: pinned-left, center, pinned-right.
 * Excludes the synthetic 'rowNumber' column.
 */
export function computeVisualColIds(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<any, any>[],
  pinnedLeftSet: Set<string>,
  pinnedRightSet: Set<string>,
): string[] {
  const left: string[] = [], center: string[] = [], right: string[] = [];
  for (const col of columns) {
    const cid = getColumnId(col);
    if (cid === 'rowNumber') continue;
    if (pinnedLeftSet.has(cid)) left.push(cid);
    else if (pinnedRightSet.has(cid)) right.push(cid);
    else center.push(cid);
  }
  return [...left, ...center, ...right];
}

/**
 * Bucket selected cells into row/column grid with "smart compression" invariants:
 * - Rows are compacted (no empty gap rows for unselected rows)
 * - Column positions are preserved (empty cells fill gaps between selected columns)
 */
function collectSelectionGrid<T extends object>(
  cellSet: Set<string>,
  colIds: string[],
  colIdToIndex: Map<string, number>,
  data: T[],
  formatValue?: (item: T, colId: string) => string,
): { sortedRows: number[]; sortedCols: number[]; rowMap: Map<number, Map<number, string>> } {
  const rowMap = new Map<number, Map<number, string>>();
  const allColIndices = new Set<number>();

  for (const ck of cellSet) {
    const sepIdx = ck.indexOf(':');
    const rowIdx = parseInt(ck.slice(0, sepIdx), 10);
    const colId = ck.slice(sepIdx + 1);
    const colIdx = colIdToIndex.get(colId) ?? colIds.indexOf(colId);
    const item = data[rowIdx];
    if (!item || colIdx < 0) continue;
    if (!rowMap.has(rowIdx)) rowMap.set(rowIdx, new Map());

    const display = formatValue ? formatValue(item, colId) : defaultFormat(item, colId);

    rowMap.get(rowIdx)!.set(colIdx, display);
    allColIndices.add(colIdx);
  }

  return {
    sortedRows: [...rowMap.keys()].sort((a, b) => a - b),
    sortedCols: [...allColIndices].sort((a, b) => a - b),
    rowMap,
  };
}

/**
 * Build a TSV string from selected cells with "smart compression":
 * - Rows are compacted (no empty gap rows for unselected rows)
 * - Column positions are preserved (empty cells fill gaps between selected columns)
 * - Optionally prepends a header row
 *
 * @param cellSet       Set of "row:colId" keys
 * @param colIds        Column IDs in visual order (from computeVisualColIds)
 * @param colIdToIndex  Map<colId, numericIndex> for O(1) lookup
 * @param data          Current sorted data array
 * @param options       Optional formatting and header options
 */
export function buildSelectionText<T extends object>(
  cellSet: Set<string>,
  colIds: string[],
  colIdToIndex: Map<string, number>,
  data: T[],
  options?: {
    formatValue?: (item: T, colId: string) => string;
    withHeaders?: boolean;
    getHeader?: (colId: string) => string;
  },
): string {
  if (cellSet.size === 0) return '';

  const { sortedRows, sortedCols, rowMap } = collectSelectionGrid(
    cellSet, colIds, colIdToIndex, data, options?.formatValue,
  );

  const lines: string[] = [];

  if (options?.withHeaders && options.getHeader) {
    lines.push(sortedCols.map(c => guardTsvField(options.getHeader!(colIds[c] ?? '') ?? '')).join('\t'));
  }

  for (const r of sortedRows) {
    lines.push(sortedCols.map(c => guardTsvField(rowMap.get(r)?.get(c) ?? '')).join('\t'));
  }

  return lines.join('\n');
}

/**
 * Copy selected cells to clipboard as tab-separated text.
 *
 * @param cellSet  Set of "row:colId" keys from useCellSelection.getSelectedCellsAsSet()
 * @param colIds   Column IDs in visual order (from computeVisualColIds)
 * @param colIdToIndex  Map<colId, numericIndex> for O(1) lookup
 * @param dataRef  Ref to the current sorted data array
 * @param formatValue  Optional formatter: (item, colId) => display string
 */
export function copySelectionToClipboard<T extends object>(
  cellSet: Set<string>,
  colIds: string[],
  colIdToIndex: Map<string, number>,
  dataRef: React.RefObject<T[]>,
  formatValue?: (item: T, colId: string) => string,
): void {
  const text = buildSelectionText(cellSet, colIds, colIdToIndex, dataRef.current ?? [], {
    formatValue,
  });
  if (text) {
    navigator.clipboard.writeText(text).catch((err) => {
      console.warn('Failed to copy to clipboard:', err);
    });
  }
}

/**
 * Escape a field for CSV output per RFC 4180 plus formula-injection guard.
 *
 * RFC 4180: wraps in double quotes and doubles inner quotes if the value
 * contains comma, quote, CR, or LF.
 *
 * Formula guard (CWE-1236): values starting with `=`, `+`, `-`, `@`, TAB or
 * CR are prefixed with a leading single quote inside quotes so Excel / Sheets
 * do not evaluate them as formulas when the CSV is opened.
 */
export function escapeCsvField(value: string): string {
  if (value === '') return '';
  if (/^[=+\-@\t\r]/.test(value)) {
    return `"'${value.replace(/"/g, '""')}"`;
  }
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Formula-injection guard for plain-text (TSV / clipboard) values — mirrors
 * escapeCsvField's leading-char check (CWE-1236). When a value starts with
 * `=`, `+`, `-`, `@`, TAB or CR, prepends a single quote so Excel / Sheets
 * do not evaluate it as a formula on paste. No quote wrapping: TSV has no
 * standard escape mechanism, so we only neutralise the leading character.
 */
export function guardTsvField(value: string): string {
  if (value === '') return '';
  if (/^[=+\-@\t\r]/.test(value)) return `'${value}`;
  return value;
}

/**
 * Copy structured fields to clipboard as TSV, guarding each field against
 * formula injection (CWE-1236). Shape covers the three single-path context
 * menu shapes: single cell `[[value]]`, cell + header `[[label], [value]]`,
 * entire row `[headers, values]`. Fields join with `\t`, rows with `\n`.
 * For selection-grid paths, use `copySelectionToClipboard` / `buildSelectionText`
 * instead — they apply guarding per-cell during grid assembly.
 */
export function copyTsvToClipboard(lines: string[][]): Promise<void> {
  const text = lines.map(row => row.map(guardTsvField).join('\t')).join('\n');
  return navigator.clipboard.writeText(text);
}

/**
 * Build a CSV string from selected cells using the same structure-preservation
 * invariants as buildSelectionText, but with comma delimiter, RFC 4180 escaping,
 * and CRLF line endings.
 */
export function buildSelectionCsv<T extends object>(
  cellSet: Set<string>,
  colIds: string[],
  colIdToIndex: Map<string, number>,
  data: T[],
  options?: {
    formatValue?: (item: T, colId: string) => string;
    withHeaders?: boolean;
    getHeader?: (colId: string) => string;
  },
): string {
  if (cellSet.size === 0) return '';

  const { sortedRows, sortedCols, rowMap } = collectSelectionGrid(
    cellSet, colIds, colIdToIndex, data, options?.formatValue,
  );

  const lines: string[] = [];

  if (options?.withHeaders && options.getHeader) {
    lines.push(sortedCols.map(c => escapeCsvField(options.getHeader!(colIds[c] ?? '') ?? '')).join(','));
  }

  for (const r of sortedRows) {
    lines.push(sortedCols.map(c => escapeCsvField(rowMap.get(r)?.get(c) ?? '')).join(','));
  }

  return lines.join('\r\n');
}

/**
 * Trigger a browser download of CSV text as a file. Prepends UTF-8 BOM so
 * Excel opens non-ASCII correctly. Revokes object URL on next tick because
 * some browsers cancel the download if the URL is revoked synchronously.
 */
export function downloadCsv(csvText: string, filename: string): void {
  const blob = new Blob(['\uFEFF' + csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Build CSV from selection and trigger file download. No-op if nothing selected.
 */
export function exportSelectionToCsv<T extends object>(
  cellSet: Set<string>,
  colIds: string[],
  colIdToIndex: Map<string, number>,
  data: T[],
  options: {
    formatValue?: (item: T, colId: string) => string;
    withHeaders?: boolean;
    getHeader?: (colId: string) => string;
  } = {},
  filename: string,
): void {
  if (cellSet.size === 0) return;
  const text = buildSelectionCsv(cellSet, colIds, colIdToIndex, data, options);
  if (text) downloadCsv(text, filename);
}

/**
 * Build the standard filename used by all tables: '{tableName}_{YYYY-MM-DD}_{HHmmss}.csv'.
 * Time is local — matches what the user sees on the clock.
 */
export function makeCsvFilename(tableName: CsvTableName): string {
  return `${tableName}_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.csv`;
}

function defaultFormat(item: object, colId: string): string {
  const value = (item as Record<string, unknown>)[colId];
  return value == null ? '' : String(value);
}
